/**
 * UX Sprint 2 — Visual Bible composite overview.
 *
 * Reads from the existing /api/projects/:id/production-continuity
 * endpoint (no new endpoints) and shows a premium dashboard for:
 *   - Characters (count, approved count, AI-generated count)
 *   - Environments (count, approved count, AI-generated count)
 *   - Reference Lock (asset_upload status)
 *   - AI Generated References hint
 *
 * Clicking "Open Production Continuity" routes the user to the existing
 * `storyboard` tab where ProductionContinuityPanel renders fully —
 * including V2 Character/Environment "Generate AI Image" buttons,
 * the credit-modal, AI GENERATED REFERENCE badges, and Approve Visual.
 * V2 logic is NEVER bypassed or modified.
 */
import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Users, Mountain, ImageIcon, ShieldCheck } from "lucide-react";
import StageSummaryCard from "./StageSummaryCard";

function mapAssetStatus(raw) {
    const s = (raw || "").toLowerCase();
    if (s === "approved") return "approved";
    if (["processing", "running", "in_progress", "leased"].includes(s)) return "in_progress";
    if (["pending", "completed", "manually_completed", "changes_requested", "rejected"].includes(s)) return "needs_review";
    if (s === "failed") return "failed";
    return "not_started";
}

export default function VisualBibleWorkspace({ project, onOpenTab }) {
    const [chars, setChars] = useState([]);
    const [envs, setEnvs] = useState([]);
    const [loaded, setLoaded] = useState(false);

    useEffect(() => {
        let mounted = true;
        if (!project?.id) return;
        (async () => {
            try {
                const { data } = await api.get(`/projects/${project.id}/production-continuity`);
                if (!mounted) return;
                setChars(data?.lock?.characters || data?.characters || []);
                setEnvs(data?.lock?.environments || data?.environments || []);
            } catch (e) {
                /* read-only summary — silent fail keeps the workspace usable. */
            } finally {
                if (mounted) setLoaded(true);
            }
        })();
        return () => { mounted = false; };
    }, [project?.id]);

    const charApproved = chars.filter((c) => (c.approval_status || "").toLowerCase() === "approved").length;
    const charAiRef = chars.filter((c) => !!c.generated_image_asset_id).length;
    const envApproved = envs.filter((e) => (e.approval_status || "").toLowerCase() === "approved").length;
    const envAiRef = envs.filter((e) => !!e.generated_image_asset_id).length;

    const charsAllReady = chars.length > 0 && charApproved === chars.length;
    const envsAllReady = envs.length > 0 && envApproved === envs.length;
    const refLockStatus = mapAssetStatus(project?.stages?.asset_upload?.status);

    return (
        <div className="px-6 lg:px-10 py-8" data-testid="visual-bible-workspace">
            <div className="mb-5">
                <div className="label-micro mb-1 text-zinc-500">Visual Bible · Overview</div>
                <p className="text-sm text-zinc-400 max-w-2xl">
                    Approve every character and environment before the storyboard goes to video. AI generation is manual — one click, one image, every time.
                </p>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-5">
                <StageSummaryCard
                    title="Characters"
                    subtitle={loaded ? `${chars.length} total · ${charApproved} approved · ${charAiRef} with AI reference` : "Loading…"}
                    status={charsAllReady ? "approved" : chars.length === 0 ? "not_started" : "needs_review"}
                    onOpen={() => onOpenTab && onOpenTab("storyboard")}
                    openLabel="Open Production Continuity"
                    testid="visual-card-characters"
                >
                    <div className="flex items-start gap-2">
                        <Users className="w-4 h-4 mt-0.5 text-zinc-500" />
                        <span>
                            {chars.length === 0
                                ? "No characters yet. Open Production Continuity to add."
                                : charsAllReady
                                    ? "Every character has a visual reference and is approved."
                                    : "Generate or upload a reference image, then approve each character."}
                        </span>
                    </div>
                </StageSummaryCard>

                <StageSummaryCard
                    title="Environments"
                    subtitle={loaded ? `${envs.length} total · ${envApproved} approved · ${envAiRef} with AI reference` : "Loading…"}
                    status={envsAllReady ? "approved" : envs.length === 0 ? "not_started" : "needs_review"}
                    onOpen={() => onOpenTab && onOpenTab("storyboard")}
                    openLabel="Open Production Continuity"
                    testid="visual-card-environments"
                >
                    <div className="flex items-start gap-2">
                        <Mountain className="w-4 h-4 mt-0.5 text-zinc-500" />
                        <span>
                            {envs.length === 0
                                ? "No environments yet. Open Production Continuity to add."
                                : envsAllReady
                                    ? "Every environment has a visual reference and is approved."
                                    : "Generate or upload a reference image, then approve each environment."}
                        </span>
                    </div>
                </StageSummaryCard>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                <StageSummaryCard
                    title="AI Generated References"
                    subtitle="Manual generation only · uses AI credits"
                    status={(charAiRef + envAiRef) > 0 ? "approved" : "not_started"}
                    onOpen={() => onOpenTab && onOpenTab("storyboard")}
                    openLabel="Open to Generate"
                    testid="visual-card-ai-refs"
                >
                    <div className="flex items-start gap-2">
                        <ImageIcon className="w-4 h-4 mt-0.5 text-zinc-500" />
                        <span>
                            {(charAiRef + envAiRef)} of {chars.length + envs.length} subjects have an AI-generated reference. Generation is one click = one image; no auto-runs.
                        </span>
                    </div>
                </StageSummaryCard>

                <StageSummaryCard
                    title="Reference Lock"
                    subtitle="Upload approved final assets to lock the project visual identity."
                    status={refLockStatus}
                    onOpen={() => onOpenTab && onOpenTab("assets")}
                    openLabel="Open Reference Lock"
                    testid="visual-card-reference-lock"
                >
                    <div className="flex items-start gap-2">
                        <ShieldCheck className="w-4 h-4 mt-0.5 text-zinc-500" />
                        <span>
                            {refLockStatus === "approved"
                                ? "Reference Lock approved."
                                : refLockStatus === "needs_review"
                                    ? "Assets uploaded — pending approval."
                                    : "Open Reference Lock to upload or finalize assets."}
                        </span>
                    </div>
                </StageSummaryCard>
            </div>
        </div>
    );
}
